import { useBusinessContext } from '@root/context'
import { useQueryParam } from '@hooks'
import { fetchAccountingRule, IAccountingRule, toHttpError } from '@root/query'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'

const parseRuleId = (value: unknown): number | undefined => {
  const parsed = parseInt(String(value), 10)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Opens the accounting-rule editor from `?id=` (audit-trail deep links and list edit).
 * Create mode is local state so it does not collide with the edit query param.
 */
export const useOpenAccountingRuleFromQuery = (createRequest?: number) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const [ruleId, setRuleId] = useQueryParam<number>('id', parseRuleId)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [deleteRule, setDeleteRule] = useState<IAccountingRule | undefined>(undefined)
  const [seenCreateRequest, setSeenCreateRequest] = useState(createRequest)
  const toastedMissingId = useRef<number | undefined>(undefined)

  if (createRequest != null && createRequest !== seenCreateRequest) {
    setSeenCreateRequest(createRequest)
    setDeleteRule(undefined)
    setIsCreateOpen(true)
  }

  // Clear the edit deep-link when create mode takes over (avoid history during render).
  useEffect(() => {
    if (isCreateOpen && ruleId != null) {
      setRuleId(undefined, { replace: true })
    }
  }, [isCreateOpen, ruleId, setRuleId])

  const ruleQuery = useQuery({
    queryKey: [slug, 'accounting-rule', ruleId],
    queryFn: () => fetchAccountingRule({ slug, id: ruleId as number }),
    enabled: ruleId != null && !isCreateOpen,
    retry: false
  })

  useEffect(() => {
    if (ruleId == null || !ruleQuery.isError) {
      if (ruleId == null) {
        toastedMissingId.current = undefined
      }
      return
    }
    if (toastedMissingId.current === ruleId) {
      return
    }
    toastedMissingId.current = ruleId
    let status = 0
    try {
      status = toHttpError(ruleQuery.error).status
    } catch {
      status = 0
    }
    if (status === 404) {
      toast.error(t('components.auditTrail.noRuleFoundInfo'))
    } else {
      toast.error(t('general.error'))
    }
    setRuleId(undefined, { replace: true })
  }, [ruleId, ruleQuery.isError, ruleQuery.error, setRuleId, t])

  const onEditRule = useCallback(
    (rule: IAccountingRule) => {
      setIsCreateOpen(false)
      setDeleteRule(undefined)
      setRuleId(rule.id)
    },
    [setRuleId]
  )

  const onDeleteRule = useCallback(
    (rule: IAccountingRule) => {
      setIsCreateOpen(false)
      setDeleteRule(rule)
      if (ruleId != null) {
        setRuleId(undefined, { replace: true })
      }
    },
    [ruleId, setRuleId]
  )

  const onFormClose = useCallback(() => {
    setIsCreateOpen(false)
    setDeleteRule(undefined)
    if (ruleId != null) {
      setRuleId(undefined, { replace: true })
    }
  }, [ruleId, setRuleId])

  const onCreateRule = useCallback(() => {
    setDeleteRule(undefined)
    setIsCreateOpen(true)
  }, [])

  const loadedRule = ruleId != null && !isCreateOpen ? ruleQuery.data : undefined
  const isFormOpen = isCreateOpen || loadedRule != null
  const formRule = isCreateOpen ? undefined : loadedRule

  return {
    isFormOpen,
    formRule,
    deleteRule,
    isDeleteFormOpen: deleteRule != null,
    onEditRule,
    onDeleteRule,
    onFormClose,
    onCreateRule
  }
}
